import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminSidebar from "./Admin/AdminSidebar";
import TopNavbar, { TOPBAR_HEIGHT } from "./Layout/TopNavbar";
import EmployeeSidebar from "./Employee/EmployeeSidebar";
import EmployeeDashboard from "./Employee/EmployeeDashboard";
import ManageEmployees from "./Admin/ManageEmployees";
import RolePermissions from "./Admin/RolePermissions";
import CreateMailAccount from "./Admin/CreateMailAccount";
import TimesheetMaster from "./Admin/TimesheetMaster";
import EmployeeLogSummary from "./Admin/EmployeeLogSummary";
import Timesheet from "./Timesheet/Timesheet";
import EmployeeLog from "./Timesheet/EmployeeLog";
import GiveReview from "./Review/GiveReview";
import MyReview from "./Review/MyReview";
import EmailPage from "./Email/EmailPage";
import HardwareMaster from "./Assets/HardwareMaster";
import HardwareDestroyed from "./Assets/HardwareDestroyed";
import HardwareEmployee from "./Assets/HardwareEmployee";
import SoftwareMaster from "./Assets/SoftwareMaster";
import LeaveApplication from "./Leave/LeaveApplication";
import LeaveManagement from "./Leave/LeaveManagement";
import MessengerSidebar from "./Messanger/MessengerSidebar";
import ChatContainer from "./Messanger/ChatContainer";
import ProjectCommunication from "./Messanger/ProjectCommunication";
import { useAuthStore } from "./store/useAuthStore";
import { useChatStore } from "./store/useChatStore";
import { connectSocket } from "./socket";
import NotesPage from "./Notes/NotesPage";
import NoteEditorPage from "./Notes/NoteEditorPage";
import HRDashboard from "./HR/HRDashboard";
import JobOpenings from "./HR/JobOpenings";
import Candidates from "./HR/Candidates";
import CandidateDetail from "./HR/CandidateDetail";
import Interviews from "./HR/Interviews";
import Offers from "./HR/Offers";
import Joining from "./HR/Joining";
import ClosedHiring from "./HR/ClosedHiring";
import RecruitmentReports from "./HR/RecruitmentReports";
import Finance from "./Finance/Finance";
import FinanceEntry from "./Finance/FinanceEntry";
import SalesDashboard from "./Finance/Sales/SalesDashboard";
import SalesMasterPage from "./Finance/Sales/SalesMasterPage";
import QuotationPage from "./Finance/Sales/QuotationPage";
import ProformaInvoicePage from "./Finance/Sales/ProformaInvoicePage";
import InvoicePage from "./Finance/Sales/InvoicePage";
import GSTReport from "./Finance/GSTReport";
import TDSReport from "./Finance/TDSReport";
import PurchaseDashboard from "./Purchase/Dashboard";
import PurchaseQuotationPage from "./Purchase/QuotationPage";
import PurchaseProformaInvoicePage from "./Purchase/ProformaInvoicePage";
import PurchaseInvoicePage from "./Purchase/InvoicePage";
import VendorMaster from "./Purchase/VendorMaster";
const API_URL =
  process.env.REACT_APP_API_URL ||
  (["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:5003"
    : window.location.origin);

/* Messenger page: contacts on the left, chat on the right
   topOffset = height of the top bar (0 for admin layout) */
function MessengerPage({ topOffset = 0 }) {
  const selectedUser = useChatStore((s) => s.selectedUser);

  return (
    <div
      style={{
        display: "flex",
        height: `calc(100vh - ${topOffset}px)`,
        width: "100%"
      }}
    >
      <div style={{ width: 360, minWidth: 320, height: "100%" }}>
        <MessengerSidebar />
      </div>

      <div style={{ flex: 1, minWidth: 0, height: "100%" }}>
        {selectedUser ? (
          <ChatContainer />
        ) : (
          <div
            style={{
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#64748b",
              fontSize: 16
            }}
          >
            Select a contact to start chatting
          </div>
        )}
      </div>
    </div>
  );
}

/* Pages shared by Admin and Employee layouts */
function AppRoutes({ user, logout, isAdmin }) {
  return (
    <Routes>
      {/* Default */}
      <Route path="/" element={<Navigate to="/admin" replace />} />

      {/* Dashboard: Admin = old dashboard, others = Employee Dashboard */}
      <Route
        path="/admin"
        element={
          isAdmin ? (
            <Dashboard user={user} onLogout={logout} />
          ) : (
            <EmployeeDashboard user={user} />
          )
        }
      />

      {/* Notes */}
      <Route path="/tools/notes" element={<NotesPage />} />
      <Route path="/tools/notes/:noteId" element={<NoteEditorPage />} />

      {/* Email (webmail inside the ERP): /email/inbox, /email/sent ... */}
      <Route path="/email" element={<Navigate to="/email/inbox" replace />} />
      <Route path="/email/*" element={<EmailPage />} />

      {/* Manage Employees + Role & Permission */}
      <Route path="/manage-employees" element={<ManageEmployees />} />
      <Route path="/role-permissions" element={<RolePermissions />} />

      {/* HR: Create Mail Account (softvolute.in only, Admin only) */}
      <Route path="/hr/create-mail-account" element={<CreateMailAccount />} />

      {/* Admin: Timesheet Master (Softvolute departments, categories, tasks) */}
      <Route path="/timesheet-master" element={<TimesheetMaster />} />

      {/* Timesheet of all employees (Employee Log Summary).
          Admin always sees it; other roles need "View All Employees" in
          Role & Permission. The page checks the access itself. */}
      <Route path="/admin/log-summary" element={<EmployeeLogSummary />} />
      <Route path="/employee-log-summary" element={<EmployeeLogSummary />} />

      {/* HR: Timesheet / Employee Log */}
      <Route path="/timesheet" element={<Timesheet />} />
      <Route path="/employee-log" element={<EmployeeLog />} />

      {/* Employee Review: my review + my goals (everyone),
          give reviews (Admin, or a role with Employee Review -> Create) */}
      <Route path="/employee-reviews" element={<MyReview />} />
      <Route path="/employee-reviews/give" element={<GiveReview />} />
      <Route path="/admin-employee-review-detail" element={<GiveReview />} />

      {/* Leave: employee applies, approver reviews */}
      <Route path="/leave-application" element={<LeaveApplication />} />
      <Route path="/leave-management" element={<LeaveManagement />} />

      {/* Assets: Software / Hardware */}
      <Route path="/software" element={<SoftwareMaster />} />
      <Route path="/hardware" element={<HardwareMaster />} />
      <Route path="/hardware-destroyed" element={<HardwareDestroyed />} />
      <Route path="/my-hardware" element={<HardwareEmployee />} />

      {/* HR / Hiring Management */}
      <Route path="/hr-dashboard" element={<HRDashboard />} />
      <Route path="/hr/job-openings" element={<JobOpenings />} />
      <Route path="/hr/candidates" element={<Candidates />} />
      <Route path="/hr/candidates/:candidateId" element={<CandidateDetail />} />
      <Route path="/hr/interviews" element={<Interviews />} />
      <Route path="/hr/offers" element={<Offers />} />
      <Route path="/hr/joining" element={<Joining />} />
      <Route path="/hr/closed-hiring" element={<ClosedHiring />} />
      <Route path="/hr/recruitment-reports" element={<RecruitmentReports />} />

      {/* Finance */}
      <Route path="/finance" element={<Finance />} />
      <Route path="/finance-entry" element={<FinanceEntry user={user} />} />

      {/* Finance > Report > GST (sidebar: R1 Sales / 3B Purchase) */}
      <Route
        path="/finance/reports/gst/r1-sales"
        element={<GSTReport initialTab="sales" />}
      />
      <Route
        path="/finance/reports/gst/3b-purchase"
        element={<GSTReport initialTab="purchase" />}
      />

      {/* Finance > Report > TDS (sidebar: TDS Receivable / TDS Payment) */}
      <Route
        path="/finance/reports/tds/receivable"
        element={<TDSReport initialTab="receivable" />}
      />
      <Route
        path="/finance/reports/tds/payable"
        element={<TDSReport initialTab="payable" />}
      />

      {/* Finance > Sales */}
      <Route path="/sales/dashboard" element={<SalesDashboard />} />
      <Route path="/sales/services" element={<SalesMasterPage type="services" />} />
      <Route path="/sales/clients" element={<SalesMasterPage type="clients" />} />
      <Route path="/sales/banks" element={<SalesMasterPage type="banks" />} />
      <Route path="/sales/terms" element={<SalesMasterPage type="terms" />} />
      <Route path="/sales/quotations" element={<QuotationPage />} />
      <Route path="/sales/proforma-invoices" element={<ProformaInvoicePage />} />
      <Route path="/sales/invoices" element={<InvoicePage />} />

      {/* Finance > Purchase */}
      <Route path="/purchase" element={<Navigate to="/purchase/dashboard" replace />} />
      <Route path="/purchase/dashboard" element={<PurchaseDashboard />} />
      <Route path="/purchase/quotations" element={<PurchaseQuotationPage />} />
      <Route
        path="/purchase/proforma-invoices"
        element={<PurchaseProformaInvoicePage />}
      />
      <Route path="/purchase/invoices" element={<PurchaseInvoicePage />} />
      <Route path="/purchase/vendors" element={<VendorMaster />} />

      {/* Messenger - User Message */}
      <Route
        path="/messenger"
        element={<MessengerPage topOffset={isAdmin ? 0 : TOPBAR_HEIGHT} />}
      />

      {/* Messenger - Project Communication */}
      <Route
        path="/messenger/project-communication"
        element={<ProjectCommunication />}
      />

      {/* Unknown Route */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const logout = () => {
    sessionStorage.removeItem("softvolute_token");
    localStorage.removeItem("token");
    useAuthStore.getState().setAuthUser(null);
    setUser(null);
  };

  const handleLogin = (u) => {
    // keep the chat stores in sync with this login
    useAuthStore.getState().setAuthUser(u);
    if (u?._id) connectSocket(u._id);
    setUser(u);
  };

  useEffect(() => {
    const token =
      localStorage.getItem("token") ||
      sessionStorage.getItem("softvolute_token");

    if (!token) {
      setChecking(false);
      return;
    }

    const controller = new AbortController();

    fetch(`${API_URL}/api/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`
      },
      signal: controller.signal
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error("Session expired");
        }
        return res.json();
      })
      .then((data) => {
        localStorage.setItem("token", token);
        useAuthStore.getState().setAuthUser(data.user);
        if (data.user?._id) connectSocket(data.user._id);
        setUser(data.user);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          sessionStorage.removeItem("softvolute_token");
          localStorage.removeItem("token");
          setUser(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setChecking(false);
        }
      });

    return () => controller.abort();
  }, []);

  if (checking) {
    return (
      <div
        style={{
          height: "100vh",
          display: "grid",
          placeItems: "center",
          color: "#343a76",
          fontWeight: 600
        }}
      >
        Loading Softvolute ERP...
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdmin = String(user.role || "").trim().toLowerCase() === "admin";

  /* ================= ADMIN LAYOUT (old sidebar + old dashboard) ================= */
  if (isAdmin) {
    return (
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          width: "100%",
          background: "#f4f6fb"
        }}
      >
        {/* Admin Sidebar */}
        <div
          style={{
            width: 280,
            minWidth: 280,
            height: "100vh",
            position: "sticky",
            top: 0,
            overflow: "hidden",
            background: "#2f3470"
          }}
        >
          <AdminSidebar user={user} onLogout={logout} />
        </div>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: "100vh",
            overflowX: "hidden"
          }}
        >
          <AppRoutes user={user} logout={logout} isAdmin />
        </main>
      </div>
    );
  }

  /* ================= EMPLOYEE LAYOUT (top bar + employee sidebar) ================= */
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        width: "100%",
        background: "#f4f6fb"
      }}
    >
      {/* Top bar */}
      <TopNavbar user={user} onLogout={logout} />

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Employee Sidebar */}
        <div
          style={{
            width: 250,
            minWidth: 250,
            height: `calc(100vh - ${TOPBAR_HEIGHT}px)`,
            position: "sticky",
            top: TOPBAR_HEIGHT,
            overflowY: "auto",
            background: "#ffffff"
          }}
        >
          <EmployeeSidebar />
        </div>

        {/* Main Content */}
        <main
          style={{
            flex: 1,
            minWidth: 0,
            overflowX: "hidden"
          }}
        >
          <AppRoutes user={user} logout={logout} isAdmin={false} />
        </main>
      </div>
    </div>
  );
}

export default App;